import type { CmmdBoardTask, CmmdTaskWorkflow } from "@cmmd-forge/contracts";
import type { ReactNode } from "react";
import { groupBoardTasksByStatus } from "~/components/projects/boardColumns";
import { ProjectTaskBoardStatusIcon } from "./ProjectTaskMilestoneBoardIcons";
import { workflowGroupForTask, workflowGroupsForTasks } from "./taskWorkflowGroups";

export interface MilestoneStatusColumn {
  readonly status: string;
  readonly label: string;
  readonly leading: ReactNode;
  readonly tasks: readonly CmmdBoardTask[];
}

function workflowStageLeading(color: string): ReactNode {
  return (
    <span
      className="inline-flex size-3 shrink-0 rounded-full border border-border"
      style={{ backgroundColor: color.startsWith("#") ? color : undefined }}
      aria-hidden="true"
    />
  );
}

export function milestoneStatusColumns(
  tasks: readonly CmmdBoardTask[],
  workflow: CmmdTaskWorkflow | null | undefined,
): readonly MilestoneStatusColumn[] {
  if (workflow == null) {
    return groupBoardTasksByStatus(tasks).map((column) =>
      Object.assign({}, column, { leading: <ProjectTaskBoardStatusIcon status={column.status} /> }),
    );
  }
  const boardStages = workflow.stages.filter((stage) => stage.category !== "backlog");
  const workflowStageIds = new Set(workflow.stages.map((stage) => stage.id));
  return [
    ...boardStages.map((stage) => ({
      status: stage.id,
      label: stage.name,
      leading: workflowStageLeading(stage.color),
      tasks: tasks.filter((task) => task.workflowStageId === stage.id),
    })),
    ...workflowGroupsForTasks(tasks, workflow)
      .filter((group) => !workflowStageIds.has(group.key) && group.key !== "backlog")
      .map((group) => ({
        status: group.key,
        label: group.label,
        leading: group.leading,
        tasks: tasks.filter((task) => workflowGroupForTask(task, workflow).key === group.key),
      })),
  ];
}
